import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiPackage, FiSearch, FiPlus, FiMinus, FiEdit, FiTrash2 } from 'react-icons/fi';
import { inventarioService, productosService } from '../../services';
import { LoadingGrid } from '../../components/UI/Loading';
import { useDebounce } from '../../hooks/useDebounce';

const AjusteStockForm = () => {
  const navigate = useNavigate();
  const searchRef = useRef(null);

  // Estados principales
  const [formData, setFormData] = useState({
    operation: 'add',
    reason: '',
    products: [] // Array de productos ajustados
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Búsqueda de productos
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const debouncedSearch = useDebounce(searchTerm, 300);

  // Listas de razones según operación
  const stockOperations = inventarioService.getStockOperations();
  const stockReasons = inventarioService.getStockReasons();

  useEffect(() => {
    if (debouncedSearch) {
      searchProducts(debouncedSearch);
    } else {
      setSearchResults([]);
    }
  }, [debouncedSearch]);

  // Click fuera del dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchProducts = async (term) => {
    try {
      const result = await productosService.getProductos({ search: term, limit: 10 });
      if (result.success) {
        setSearchResults(result.data || []);
        setShowDropdown(true);
      }
    } catch (error) {
      console.error('Error buscando productos:', error);
    }
  };

  const handleSelectProduct = (product) => {
    // Verificar que no esté ya agregado
    const exists = formData.products.find(p => p.product_id === product.id);
    if (exists) {
      alert('Este producto ya está en la lista');
      return;
    }

    // Agregar producto a la lista
    setFormData({
      ...formData,
      products: [
        ...formData.products,
        {
          product_id: product.id,
          product_name: product.name,
          product_sku: product.code || product.barcode || 'N/A',
          current_stock: product.current_stock || 0,
          quantity: 1,
          notes: '',
          unit_cost: formData.operation === 'add' ? '' : null
        }
      ]
    });

    setSearchTerm('');
    setShowDropdown(false);
  };

  const handleOperationChange = (operation) => {
    setFormData({
      ...formData,
      operation,
      reason: '' // Reset reason cuando cambia operación
    });
  };

  const handleProductChange = (index, field, value) => {
    const updatedProducts = [...formData.products];
    updatedProducts[index][field] = value;
    setFormData({ ...formData, products: updatedProducts });
  };

  const handleRemoveProduct = (index) => {
    const updatedProducts = formData.products.filter((_, i) => i !== index);
    setFormData({ ...formData, products: updatedProducts });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.products.length === 0) {
      alert('Debe agregar al menos un producto');
      return;
    }

    if (!formData.reason) {
      alert('Debe seleccionar una razón del movimiento');
      return;
    }

    // Validar que todos los productos tengan cantidad
    const invalidProducts = formData.products.filter(p => !p.quantity || p.quantity <= 0);
    if (invalidProducts.length > 0) {
      alert('Todos los productos deben tener una cantidad mayor a 0');
      return;
    }

    setIsSaving(true);

    try {
      // Preparar datos para el endpoint bulk
      const bulkData = {
        operation: formData.operation,
        reason: formData.reason,
        products: formData.products.map(p => ({
          product_id: p.product_id,
          quantity: parseInt(p.quantity),
          notes: p.notes || '',
          unit_cost: p.unit_cost ? parseFloat(p.unit_cost) : undefined
        }))
      };

      const result = await inventarioService.bulkStockUpdate(bulkData);

      if (result.success) {
        alert('Stock ajustado correctamente para todos los productos');
        navigate('/system/inventario');
      } else {
        alert(result.message || 'Error al ajustar stock');
      }
    } catch (error) {
      alert('Error al ajustar stock: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/system/inventario');
  };

  const calculateNewStock = (currentStock, quantity, operation) => {
    const current = parseInt(currentStock) || 0;
    const qty = parseInt(quantity) || 0;

    switch (operation) {
      case 'add':
        return current + qty;
      case 'subtract':
        return current - qty;
      case 'set':
        return qty;
      default:
        return current;
    }
  };

  if (isLoading) {
    return <LoadingGrid message="Cargando..." />;
  }

  return (
    <div className="ajuste-stock-form-page">
      {/* Header */}
      <div className="form-header">
        <button className="btn-back" onClick={handleCancel}>
          <FiArrowLeft /> Volver
        </button>
        <h1 className="form-title">Ajuste Masivo de Stock</h1>
        <div className="form-actions">
          <button type="button" className="btn btn--ghost" onClick={handleCancel}>
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn--primary"
            onClick={handleSubmit}
            disabled={isSaving}
          >
            <FiSave /> {isSaving ? 'Guardando...' : 'Guardar Ajuste'}
          </button>
        </div>
      </div>

      {/* Formulario */}
      <div className="form-content">
        {/* Card 1: Información del Ajuste */}
        <div className="info-card">
          <div className="card-header">
            <div className="icon-circle icon-circle--primary">
              <FiPackage size={24} />
            </div>
            <div className="header-text">
              <h2>Información del Ajuste</h2>
              <p>Configure el tipo de operación y la razón del movimiento</p>
            </div>
          </div>

          <div className="card-body">
            <div className="form-grid">
              {/* Tipo de Operación */}
              <div className="form-group full-width">
                <label className="form-label">
                  Tipo de Operación <span className="required">*</span>
                </label>
                <div className="operation-buttons">
                  {stockOperations.map((op) => (
                    <button
                      key={op.value}
                      type="button"
                      className={`operation-btn ${formData.operation === op.value ? 'active' : ''}`}
                      onClick={() => handleOperationChange(op.value)}
                    >
                      {op.value === 'add' && <FiPlus />}
                      {op.value === 'subtract' && <FiMinus />}
                      {op.value === 'set' && <FiEdit />}
                      {op.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Razón */}
              <div className="form-group full-width">
                <label className="form-label">
                  Razón del Movimiento <span className="required">*</span>
                </label>
                <select
                  className="form-control"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  required
                >
                  <option value="">Seleccione una razón</option>
                  {stockReasons[formData.operation]?.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: Búsqueda de Producto */}
        <div className="search-card">
          <div className="card-header">
            <h3>Buscar Productos</h3>
          </div>

          <div className="card-body">
            <div className="product-search-wrapper" ref={searchRef}>
              <div className="search-input-group">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  className="search-input"
                  placeholder="Buscar por nombre, código o SKU..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                />
              </div>

              {/* Dropdown de resultados */}
              {showDropdown && searchResults.length > 0 && (
                <div className="search-dropdown">
                  {searchResults.map((product) => (
                    <div
                      key={product.id}
                      className="search-result-item"
                      onClick={() => handleSelectProduct(product)}
                    >
                      <div className="result-info">
                        <div className="result-name">{product.name}</div>
                        <div className="result-details">
                          SKU: {product.code || product.barcode || 'N/A'} | Stock: {product.current_stock || 0}
                        </div>
                      </div>
                      <div className="result-price">
                        <FiPlus />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Card 3: Tabla de Productos */}
        <div className="products-table-card">
          <div className="card-header">
            <h3>Productos a Ajustar ({formData.products.length})</h3>
          </div>

          <div className="card-body">
            {formData.products.length === 0 ? (
              <div className="empty-state">
                <FiPackage size={48} />
                <p>No hay productos agregados</p>
                <small>Use el buscador para agregar productos al ajuste</small>
              </div>
            ) : (
              <div className="products-table-wrapper">
                <table className="products-table">
                  <thead>
                    <tr>
                      <th style={{ width: '35%' }}>Producto</th>
                      <th style={{ width: '10%', textAlign: 'center' }}>Stock Actual</th>
                      <th style={{ width: '15%', textAlign: 'center' }}>Cantidad</th>
                      {formData.operation === 'add' && (
                        <th style={{ width: '15%', textAlign: 'center' }}>Costo Unit.</th>
                      )}
                      <th style={{ width: '10%', textAlign: 'center' }}>Nuevo Stock</th>
                      <th style={{ width: '20%' }}>Notas</th>
                      <th style={{ width: '5%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.products.map((product, index) => (
                      <tr key={index}>
                        <td>
                          <div className="product-cell">
                            <div className="product-name">{product.product_name}</div>
                            <div className="product-sku">SKU: {product.product_sku}</div>
                          </div>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className="stock-badge">{product.current_stock}</span>
                        </td>
                        <td>
                          <input
                            type="number"
                            className="table-input"
                            value={product.quantity}
                            onChange={(e) => handleProductChange(index, 'quantity', e.target.value)}
                            min="1"
                            required
                          />
                        </td>
                        {formData.operation === 'add' && (
                          <td>
                            <input
                              type="number"
                              className="table-input"
                              value={product.unit_cost || ''}
                              onChange={(e) => handleProductChange(index, 'unit_cost', e.target.value)}
                              placeholder="0.00"
                              step="0.01"
                            />
                          </td>
                        )}
                        <td style={{ textAlign: 'center' }}>
                          <span className={`new-stock-badge new-stock-badge--${formData.operation}`}>
                            {calculateNewStock(product.current_stock, product.quantity, formData.operation)}
                          </span>
                        </td>
                        <td>
                          <input
                            type="text"
                            className="table-input"
                            value={product.notes}
                            onChange={(e) => handleProductChange(index, 'notes', e.target.value)}
                            placeholder="Opcional..."
                          />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            type="button"
                            className="btn-remove"
                            onClick={() => handleRemoveProduct(index)}
                            title="Eliminar"
                          >
                            <FiTrash2 />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AjusteStockForm;
